import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { CommentBank as CommentBankIcon, LiveHelp as LiveHelpIcon } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import NewSystemFieldSVG from './NewSystemFieldSVG';
import NewField from './Modals/NewField';
import QuestionLibraryModal from './Modals/QuestionLibraryModal';
import AddSystemField from './Modals/AddSystemField';
import StyledSnackbar from '../../Components/CustomUIElements/StyledSnackbar';
import AIStudioField from '../../../AIStudio/AIStudioQuestions/AIStudioQuestions';
import { FelixButtonSVG } from '../../../AIStudio/SharedComponents/Graphics/FelixSVG';
import { classes } from './styles';
import { classes as AIClasses } from '../styles';
import { KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { QuestionCardSensor } from './QuestionCardSensor';
import { IJob } from '../types';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import { QuestionList } from './QuestionList';
import { IApplicantQuestion } from '../types';
import { formatUserPermissions } from '../../Components/Utilities/formatUserPermissions';
import { useFeatureFlag } from '../../../FeatureFlags';

export default function Questions({
  job,
  jobId,
  canAddIndeedDemographicQ
}: {
  job: IJob;
  jobId: number;
  canAddIndeedDemographicQ: boolean;
}) {
  const [questions, setQuestions] = useState<IApplicantQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [selected, setSelected] = useState('enabledQuestions');
  const [modalsOpen, setModalsOpen] = useState<Record<string, boolean>>({
    newField: false,
    addSystemField: false,
    aiStudioField: false,
    questionLibrary: false
  });
  const [snackbar, setSnackbar] = useState<{
    message: string;
    state: 'success' | 'warning' | 'error';
  }>({
    message: '',
    state: 'success'
  });
  const permissions = formatUserPermissions();
  const [editingQuestion, setEditingQuestion] = useState<IApplicantQuestion | null>();
  const aiStudioEnabled = useFeatureFlag('aiStudioEnabled');

  const sensors = useSensors(
    useSensor(QuestionCardSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = questions.map((question) => question.id).indexOf(active.id);
      const newIndex = questions.map((question) => question.id).indexOf(over.id);
      const newArray = arrayMove(questions, oldIndex, newIndex);

      setQuestions(newArray);
      updateJobQuestions(newArray);
    }
  }

  const getQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/v4/fields?job_id=${jobId}`, { method: 'GET' }).then(
        (response) => response.json()
      );
      setQuestions(response.fields);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);
  const updateJobQuestions = async (questions) => {
    const fieldObject = {};
    questions.map(
      (question, index) =>
        (fieldObject[question.id] = {
          position: index
        })
    );
    try {
      const response = await fetch(`/api/v4/jobs/${jobId}/update_questions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: fieldObject
        })
      }).then((response) => response.json());
      setQuestions(response.questions);
      setSnackbar({
        message: 'Questions have been updated',
        state: 'success'
      });
    } catch (error) {
      setSnackbar({
        message: 'There was an error saving your changes',
        state: 'error'
      });
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>, newSelected: string) => {
    if (newSelected !== null) {
      setSelected(newSelected);
    }
  };

  useEffect(() => {
    getQuestions();
  }, [getQuestions]);

  return (
    <Box sx={classes.questionsPageContainer}>
      <Box sx={classes.pageHeader}>
        <h2 data-testid="questions-label">Questions</h2>
      </Box>
      <Box sx={classes.noticeContainer}>
        <InfoIcon sx={classes.noticeIcon} />
        <Box sx={{ marginLeft: 1 }}>
          Any changes (adding, editing, removing etc.) to your questions will effect and block the
          importing of applications from SEEK into :Recruit.{' '}
          <b>All changes will also be automatically saved and applied immediately.</b>
        </Box>
      </Box>
      <Box sx={classes.noticeContainer}>
        <InfoIcon sx={classes.infoIcon} />
        <Box sx={{ marginLeft: 1 }}>
          All questions that have been answered by a candidate are not able to be edited or deleted.
          To remove or edit a question, you will need to disable it and create a new question.
        </Box>
      </Box>
      <Box sx={classes.toggleButtonsContainer}>
        <ToggleButtonGroup
          value={selected}
          exclusive
          onChange={handleToggle}
          aria-label="enabled or disabled questions"
        >
          <ToggleButton value="enabledQuestions" sx={sharedClasses.toggleButton}>
            Enabled questions
          </ToggleButton>
          <ToggleButton value="disabledQuestions" sx={sharedClasses.toggleButton}>
            Disabled questions
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={classes.actionsContianer}>
        {selected === 'enabledQuestions' && (
          <Button
            data-testid="new-field-button"
            variant="outlined"
            startIcon={<LiveHelpIcon />}
            sx={classes.actionButton}
            onClick={() => {
              setEditingQuestion(null);
              setModalsOpen((prev) => ({ ...prev, newField: true }));
            }}
          >
            New question
          </Button>
        )}
        {selected === 'enabledQuestions' && (
          <Button
            data-testid="add-question-library-to-job-button"
            variant="outlined"
            startIcon={<CommentBankIcon />}
            sx={classes.actionButton}
            onClick={() => {
              setEditingQuestion(null);
              setModalsOpen((prev) => ({ ...prev, questionLibrary: true }));
            }}
          >
            Question library
          </Button>
        )}
        {selected === 'enabledQuestions' && (
          <Button
            variant="outlined"
            startIcon={<NewSystemFieldSVG />}
            sx={classes.actionButton}
            onClick={() => {
              setEditingQuestion(null);
              setModalsOpen((prev) => ({ ...prev, addSystemField: true }));
            }}
          >
            Add system field
          </Button>
        )}
        {permissions?.['AI Features']?.['Activate AI Studio'] && aiStudioEnabled && (
          <Box sx={{ marginLeft: 'auto' }}>
            <Box
              id="ai-studio-questions-integration-button"
              sx={{
                ...AIClasses.buttonContainer,
                ...(buttonHovered ? AIClasses.hoverAnimation : AIClasses.unhoverAnimation)
              }}
              onMouseEnter={() => setButtonHovered(true)}
              onMouseLeave={() => setButtonHovered(false)}
              onClick={() => {
                setEditingQuestion(null);
                setModalsOpen((prev) => ({ ...prev, aiStudioField: true }));
              }}
            >
              <Box sx={AIClasses.avatarContainer}>
                <FelixButtonSVG />
              </Box>
              <Button sx={AIClasses.button}>Generate questions</Button>
            </Box>
          </Box>
        )}
      </Box>
      <QuestionList
        enabled={selected === 'enabledQuestions'}
        getQuestions={getQuestions}
        handleDragEnd={handleDragEnd}
        isLoading={isLoading}
        job={job}
        jobId={jobId}
        questions={questions}
        sensors={sensors}
        setEditingQuestion={setEditingQuestion}
        setModalsOpen={setModalsOpen}
        setQuestions={setQuestions}
        setSnackbar={setSnackbar}
      />
      <NewField
        autoRating={job.auto_rater != null}
        editingQuestion={editingQuestion}
        getQuestions={getQuestions}
        jobId={jobId}
        modalsOpen={modalsOpen}
        setModalsOpen={setModalsOpen}
        setSnackbar={setSnackbar}
        canAddIndeedDemographicQ={canAddIndeedDemographicQ}
      />
      {modalsOpen.questionLibrary && (
        <QuestionLibraryModal
          modalsOpen={modalsOpen}
          setModalsOpen={setModalsOpen}
          setSnackbar={setSnackbar}
          getQuestions={getQuestions}
          jobId={jobId}
        />
      )}
      {aiStudioEnabled && (
        <AIStudioField
          getQuestions={getQuestions}
          modalsOpen={modalsOpen.aiStudioField}
          setModalsOpen={(open: boolean) =>
            setModalsOpen((prev) => ({ ...prev, aiStudioField: open }))
          }
          jobId={jobId}
          setSnackbar={setSnackbar}
          jobTitle={job.title}
          jobDescription={job.description}
        />
      )}
      <AddSystemField
        getQuestions={getQuestions}
        modalsOpen={modalsOpen}
        setModalsOpen={setModalsOpen}
        jobId={jobId}
        setSnackbar={setSnackbar}
      />
      <StyledSnackbar
        message={snackbar.message}
        state={snackbar.state}
        setSnackbarState={setSnackbar}
      />
    </Box>
  );
}
